/*
 * The Ticket Delivery screen's state, extracted so the classic panel and the v3 screen cannot drift.
 *
 * Wave 2 rebuilds presentation, not behaviour. Two shells render this setting during the migration,
 * and a second copy of "what counts as dirty" or "what save sends" is exactly the kind of drift that
 * ends with the two shells disagreeing about whether a change was saved. So the rule and the request
 * live here once, and each shell decides only how to draw them.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, adminApi } from '../../api';
import type { DeliveryChannel, TicketDeliveryDto, TicketDeliveryMode } from '../../api';

export interface TicketDeliveryState {
  data: TicketDeliveryDto | null;
  mode: TicketDeliveryMode;
  channels: DeliveryChannel[];
  loading: boolean;
  saving: boolean;
  saved: boolean;
  error: string;
  /** Load failed, as opposed to a save that failed — different states, different recoveries. */
  loadError: string;
  dirty: boolean;
  /** `instant_and_send` is the only mode that sends copies; the rest keep the selection but ignore it. */
  sendMode: boolean;
  /** Ticked here, switched off at platform level. Selectable, but nothing will go out that way. */
  blocked: DeliveryChannel[];
  setMode: (next: TicketDeliveryMode) => void;
  toggleChannel: (key: DeliveryChannel) => void;
  save: () => Promise<void>;
  reset: () => void;
}

export function useTicketDelivery(token: string, eventSlug?: string): TicketDeliveryState {
  const [data, setData] = useState<TicketDeliveryDto | null>(null);
  const [mode, setModeState] = useState<TicketDeliveryMode>('verified_channel');
  const [channels, setChannels] = useState<DeliveryChannel[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let live = true;
    adminApi.getTicketDelivery(token, eventSlug)
      .then((d) => {
        if (!live) return;
        setData(d); setModeState(d.mode); setChannels(d.copyChannels);
      })
      .catch((e) => {
        if (!live) return;
        setLoadError(e instanceof ApiError ? e.message : 'โหลดการตั้งค่าการส่งมอบตั๋วไม่สำเร็จ');
      });
    return () => { live = false; };
  }, [token, eventSlug]);

  const dirty = !!data && (mode !== data.mode || channels.join(',') !== data.copyChannels.join(','));
  const sendMode = mode === 'instant_and_send';
  const blocked = sendMode && data ? channels.filter((c) => !data.channelAvailability[c]) : [];

  const setMode = useCallback((next: TicketDeliveryMode) => {
    setModeState(next);
    setSaved(false);
  }, []);

  const toggleChannel = useCallback((key: DeliveryChannel) => {
    setSaved(false);
    setChannels((prev) => (prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]));
  }, []);

  const reset = useCallback(() => {
    if (!data) return;
    setModeState(data.mode);
    setChannels(data.copyChannels);
    setSaved(false);
    setError('');
  }, [data]);

  const save = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const next = await adminApi.setTicketDelivery(token, { mode, copyChannels: channels }, eventSlug);
      setData(next);
      setModeState(next.mode);
      setChannels(next.copyChannels);
      setSaved(true);
    } catch (e) {
      // The values the person chose are left exactly as they are: a failed save must not silently
      // roll the form back to what the server last said.
      setError(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }, [token, eventSlug, mode, channels]);

  return {
    data, mode, channels,
    loading: !data && !loadError,
    saving, saved, error, loadError,
    dirty, sendMode, blocked,
    setMode, toggleChannel, save, reset,
  };
}
